/**
 * Hook to orchestrate the "AsyncAutocompleteField" component. This hook is not supposed to be used out of it.
 */

import Autocomplete from '@mui/material/Autocomplete';
import {
  InfiniteData,
  skipToken,
  useInfiniteQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { debounce, isEqual } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  DEFAULT_API_PAGE_SIZE,
  GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS,
} from '~constants/consts';
import { InfiniteQueryParamsState } from '~queries/constants';
import { extractPagination } from '~services/pagination';
import { usePrevious } from '~utils/hooks/usePrevious';

import {
  AutocompleteFieldOption,
  FetchFnQueryArgument,
  UseAsyncAutocompleteFieldProps,
} from './types';

type OptionsPage<ParsedType> = {
  nextCursor?: string;
  options: AutocompleteFieldOption<ParsedType>[];
};

export default function useAsyncAutocompleteField<
  ReadType,
  ParsedType,
  FetchFnExtraArguments extends Record<string, unknown>,
>({
  fetchFn,
  initialInputValue,
  onSearchChange,
  parseFn,
  queryName,
  search,
}: UseAsyncAutocompleteFieldProps<ReadType, ParsedType, FetchFnExtraArguments> & {
  initialInputValue?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);

  const [queryParams, setQueryParams] = useState<InfiniteQueryParamsState>({
    shouldFetch: false,
    filters: { query: '' },
  });

  const [uncontrolledInputValue, setUncontrolledInputValue] = useState(
    initialInputValue ?? '',
  );
  const isSearchControlled = search !== undefined;
  const inputValue = isSearchControlled ? search : uncontrolledInputValue;
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;
  const previousInputValue = usePrevious(inputValue);
  const inputValueDidChange = (previousInputValue ?? '') !== inputValue;

  const queryClient = useQueryClient();

  const requestScope = [fetchFn.arguments.path, fetchFn.arguments.query];

  const query = useInfiniteQuery<
    OptionsPage<ParsedType>,
    Error,
    InfiniteData<OptionsPage<ParsedType>>,
    [string, { requestScope: unknown[]; search: string }],
    string | undefined
  >({
    queryKey: [queryName, { requestScope, search: queryParams.filters.query }],
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    queryFn: queryParams.shouldFetch
      ? async ({ pageParam, signal }) => {
          const query: FetchFnQueryArgument = {
            'page[limit]': DEFAULT_API_PAGE_SIZE,
            'page[after]': pageParam,
            'search[query]': queryParams.filters.query.trim() || undefined,
          };

          const response = await fetchFn.request({
            ...fetchFn.arguments,
            query: { ...query, ...(fetchFn.arguments.query ?? {}) },
            signal,
          });

          const options = response.data.data.flatMap((item) => parseFn(item) ?? []);

          return { nextCursor: extractPagination(response).after, options };
        }
      : skipToken,
    // Only a new search keeps the old page on screen; options from another scope must not be pickable.
    placeholderData: (previousData, previousQuery) =>
      isEqual(previousQuery?.queryKey[1].requestScope, requestScope)
        ? previousData
        : undefined,
  });

  const debouncedSearch = useRef(
    debounce((query: string) => {
      setQueryParams({ shouldFetch: true, filters: { query } });
    }, GLOBAL_DEBOUNCE_DEBOUNCE_DELAY_IN_MS),
  ).current;

  const allOptions = useMemo(
    () => (query.data?.pages ?? []).flatMap((page) => page.options),
    [query.data?.pages],
  );

  const handleOpen = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleInputChange = useCallback<
    Exclude<React.ComponentProps<typeof Autocomplete>['onInputChange'], undefined>
  >(
    (_event, newValue, reason) => {
      if (!isSearchControlled) {
        setUncontrolledInputValue(newValue);

        return;
      }

      // MUI clears a multiple field's text on every select, blur and value change.
      const isTypedOrCleared = reason === 'input' || reason === 'clear';

      if (isTypedOrCleared) {
        onSearchChange?.(newValue);
      }
    },
    [isSearchControlled, onSearchChange],
  );

  const fetchMore = useCallback(() => {
    if (query.hasNextPage && !query.isFetching) {
      // Scroll events outrun the re-render that flips isFetching, so a later call must join the request in flight.
      query.fetchNextPage({ cancelRefetch: false });
    }
  }, [query.hasNextPage, query.isFetching, query.fetchNextPage]);

  useEffect(() => {
    if (!isOpen) {
      queryClient.setQueriesData({ queryKey: [queryName] }, () => ({
        pages: [],
        pageParams: [],
      }));
    }

    setQueryParams({
      shouldFetch: isOpen,
      filters: { query: isSearchControlled ? inputValueRef.current : '' },
    });
  }, [queryName, isOpen, isSearchControlled]);

  useEffect(() => {
    if (inputValueDidChange) {
      debouncedSearch.cancel();

      if (isOpen) {
        debouncedSearch(inputValue);
      }
    }
  }, [inputValueDidChange, inputValue, isOpen]);

  return {
    fetchMore,
    handleClose,
    handleInputChange,
    handleOpen,
    inputValue,
    isFetching: query.isFetching,
    isOpen,
    options: allOptions,
  };
}
